import * as React from "react";
import DashboardLayout from "./DashboardLayout";
import BlogModal, { BlogType } from "./BlogModal";
import { ToastContainer, toast } from "react-toastify";

// Simple skeleton row for loading state
const BlogTableSkeleton: React.FC = () => (
  <>
    {[1, 2, 3].map((i) => (
      <tr key={i} className="border-b text-center animate-pulse">
        {[10, 24, 32, 40, 16].map((w, idx) => (
          <td className="p-2 border" key={idx}>
            <div
              className={`h-4${idx === 4 ? " h-8" : ""} bg-gray-200 rounded`}
              style={{ width: idx === 4 ? 64 : w + "px", margin: "0 auto" }}
            />
          </td>
        ))}
      </tr>
    ))}
  </>
);

type ConfirmDeleteState = {
  open: boolean;
  blog: BlogType | null;
};

const Blogs: React.FC = () => {
  const [modalOpen, setModalOpen] = React.useState(false);
  const [blogs, setBlogs] = React.useState<BlogType[]>([]);
  const [editBlog, setEditBlog] = React.useState<BlogType | null>(null);
  const [loading, setLoading] = React.useState(true);
  const [confirmDelete, setConfirmDelete] = React.useState<ConfirmDeleteState>({ open: false, blog: null });

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

  React.useEffect(() => {
    async function fetchBlogs() {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE_URL}/get_blogs.php`);
        const data = await response.json();
        let blogsArray: BlogType[] = [];
        if (Array.isArray(data)) {
          blogsArray = data;
        } else if (data && typeof data === "object") {
          const arrayProp = Object.values(data).find((v) => Array.isArray(v));
          if (Array.isArray(arrayProp)) {
            blogsArray = arrayProp;
          }
        }
        setBlogs(blogsArray);
      } catch (error) {
        setBlogs([]);
        console.error("Failed to fetch blogs from API:", error);
      } finally {
        setLoading(false);
      }
    }
    fetchBlogs();
  }, [API_BASE_URL]);

  const saveBlog = React.useCallback(
    (blog: BlogType) => {
      let updatedBlogs;
      if (editBlog) {
        updatedBlogs = Array.isArray(blogs)
          ? blogs.map((b) => (b.id === blog.id ? blog : b))
          : [];
        setEditBlog(null);
      } else {
        updatedBlogs = Array.isArray(blogs) ? [...blogs, blog] : [blog];
      }
      setBlogs(updatedBlogs);
      console.log("Saved/Updated blog. Blogs list:", updatedBlogs);
    },
    [blogs, editBlog]
  );

  const handleEdit = React.useCallback((blog: BlogType) => {
    setEditBlog(blog);
    setModalOpen(true);
    console.log("Editing blog:", blog);
  }, []);

  // Show delete confirmation modal before deleting
  const handleDeleteRequest = React.useCallback(
    (blog: BlogType) => {
      setConfirmDelete({ open: true, blog });
    },
    []
  );

  // Actually delete after confirmation
  const handleDeleteConfirmed = React.useCallback(
    async () => {
      const blog = confirmDelete.blog;
      if (!blog) return;
      try {
        const response = await fetch(
          `${API_BASE_URL}/delete_blog.php`,
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ blog_uid: blog.id }),
          }
        );

        const resJson = await response.json();
        if (!resJson.success) {
          console.error("Failed to delete blog from API:", resJson);
          toast.error("Failed to delete blog. Please try again.");
          setConfirmDelete({ open: false, blog: null });
          return;
        } else {
          toast.success("Blog deleted successfully!");
        }

        const updatedBlogs = Array.isArray(blogs)
          ? blogs.filter((b) => b.id !== blog.id)
          : [];
        setBlogs(updatedBlogs);

        console.log(
          "Deleted blog with blog_uid:",
          blog.id,
          "Updated blogs list:",
          updatedBlogs
        );
      } catch (error) {
        console.error("Error deleting blog:", error);
        toast.error("An error occurred while deleting the blog.");
      } finally {
        setConfirmDelete({ open: false, blog: null });
      }
    },
    [API_BASE_URL, blogs, confirmDelete]
  );

  const blogsArray: BlogType[] = Array.isArray(blogs) ? blogs : [];

  const getSectionsArray = (sections: any): any[] =>
    Array.isArray(sections) ? sections : [];

  const getSocialLinksArray = (socialLinks: any): any[] =>
    Array.isArray(socialLinks) ? socialLinks : [];

  // Confirmation Modal
  const ConfirmDeleteModal: React.FC<{
    open: boolean;
    blog: BlogType | null;
    onConfirm: () => void;
    onCancel: () => void;
  }> = ({ open, blog, onConfirm, onCancel }) => {
    if (!open || !blog) return null;
    return (
      <div
        className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-30"
        style={{ backdropFilter: "blur(2px)" }}
      >
        <div className="bg-white rounded shadow-lg p-6 w-full max-w-sm">
          <h2 className="text-lg font-semibold mb-2">Delete Blog</h2>
          <p className="mb-4">
            Are you sure you want to delete{" "}
            <span className="font-bold text-red-700">
              {blog.title || blog.name || "this blog"}
            </span>
            ?
          </p>
          <div className="flex justify-end gap-2">
            <button
              className="px-4 py-2 border rounded"
              onClick={onCancel}
            >
              Cancel
            </button>
            <button
              className="px-4 py-2 bg-red-600 text-white rounded"
              onClick={onConfirm}
            >
              Confirm Delete
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold">Blog Pages</h2>
        <button
          className="bg-blue-600 text-white px-4 py-2 rounded shadow hover:bg-blue-700"
          onClick={() => {
            setEditBlog(null);
            setModalOpen(true);
            console.log("Opening modal to add new blog"); // Debugging output
          }}
        >
          + Add New Blog
        </button>
      </div>
      <BlogModal
        isOpen={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setEditBlog(null);
          console.log("Closed modal"); // Debugging output
        }}
        onSave={saveBlog}
        {...(editBlog ? { ...editBlog } : {})}
      />
      <div className="bg-white rounded shadow p-4 overflow-x-auto">
        <table className="min-w-full border">
          <thead>
            <tr className="bg-gray-100">
              <th className="p-2 border">ID</th>
              <th className="p-2 border">Name</th>
              <th className="p-2 border">Title</th>
              <th className="p-2 border">Description</th>
              {/* <th className="p-2 border">Social Links</th>
              <th className="p-2 border">Sections</th> */}
              <th className="p-2 border">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <BlogTableSkeleton />
            ) : blogsArray.length === 0 ? (
              <tr>
                <td colSpan={7} className="text-center p-4">
                  No blogs found.
                </td>
              </tr>
            ) : (
              blogsArray.map((blog) => (
                <tr key={blog.id} className="border-b text-center">
                  <td className="p-2 border">{blog.id}</td>
                  <td className="p-2 border">{blog.name}</td>
                  <td className="p-2 border">{blog.title}</td>
                  <td className="p-2 border">{blog.description}</td>
                  {/* <td className="p-2 border">
                    {getSocialLinksArray(blog.socialLinks).map((link, idx) => (
                      <div key={idx}>
                        <a
                          href={link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 underline"
                        >
                          {link}
                        </a>
                      </div>
                    ))}
                  </td> */}
                  {/* <td className="p-2 border">
                    {getSectionsArray(blog.sections).map((sec, idx) => (
                      <div key={idx} className="mb-2">
                        <strong>{sec && sec.heading}</strong>
                        <div>{sec && sec.description}</div>
                      </div>
                    ))}
                  </td> */}
                  <td className="p-2 border">
                    {/* Uncomment below for edit button:
                     <button
                      className="bg-yellow-500 text-white px-2 py-1 rounded mr-2"
                      onClick={() => handleEdit(blog)}
                    >
                      Edit
                    </button>
                    */}
                    <button
                      className="bg-red-500 text-white px-2 py-1 rounded"
                      onClick={() => handleDeleteRequest(blog)}
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      <ConfirmDeleteModal
        open={confirmDelete.open}
        blog={confirmDelete.blog}
        onConfirm={handleDeleteConfirmed}
        onCancel={() => setConfirmDelete({ open: false, blog: null })}
      />
      <ToastContainer />
    </DashboardLayout>
  );
};

export default Blogs;
